import { featureEnabled } from '../../functions/featureFlags.js'
// SimoneReasoningPill — THE bottom-centre island: one morphing capsule that is
// both the assistant's small view AND the minimized voice surface (T11–T14,
// tickets/ai-fable.md). Docked just above the global composer.
//
// ONE surface with CONSTANT CHROME — not a set of cases that replace each
// other. Rebuilt 2026-08-02 (Nika: "ai assistant text islands in the background
// should be consistant, logging in one small minimized thread so you can keep
// track. do not show hide different ui" / "in terms of voice and text, island
// should be transforming"). Before this the body was twelve siblings each
// gated on the active case, and RecentChat / CurrentStep / LiveText were
// MUTUALLY EXCLUSIVE — so the moment a turn started the conversation vanished
// and was replaced by a status line, then came back. That read as different
// panels swapping, which is exactly what it was.
//
// What is CONSTANT: the shell, and `Head` — ONE row at the control height,
// never hidden, whose leading indicator (star ↔ orb), status words and
// trailing buttons are the only things that change. THE BAR NEVER GROWS
// (Nika 2026-10-28: "in minimized mode, captions should be like bubble and
// should not resize ai navbar"): the bar is the head row alone — the same
// height with no turn, one turn or ten, and while a reply streams. The
// running step names itself IN the row ("Working: Searching …"), and the
// delegated agents as a count ("2 agents working…"); the drawer keeps the
// full timeline.
// What FLOATS ABOVE: `AiBubbleStack` — the newest lines as bubbles (in voice
// the engine-agnostic `voiceTurns`, otherwise the assistant conversation) and,
// nearest the bar, the step that waits for the person's OK (Nika 2026-10-28:
// "when AI asks for confirmation, in minimized mode its not visible"). An
// absolutely positioned column above the bar's top edge: no layout space.
//
// The states it serves (they change CONTENT, never the chrome):
//   • voice     — `voiceOpen && voiceMinimized && !voiceHost`. Replaces
//                 VoiceOverlay's retired `.isMini` card; the session KEEPS
//                 RUNNING on the overlay element (still the engine host — this
//                 island only renders).
//   • live      — a turn is streaming / sub-agents running while the full
//                 drawer isn't visible (the original reasoning pill).
//   • minimized — the user collapsed an OPEN assistant (assistantDock
//                 'minimized'): a persistent handle showing the thread.
//   • slot      — homeAiSend kept the drawer closed for a PageSlot sheet
//                 (_assistantPillHandle): the pill is the turn's only surface.
//   • ask       — a step waits for the person's OK while the thread is off
//                 screen: the island shows even with no handle and no live
//                 turn (the transport stops "typing" while the ask waits), the
//                 head says "Needs your OK" and the ask sits above the bar.
//   • answer    — an answer landed while the island is the thread's handle:
//                 the head says "Answer ready" and carries Open in main and
//                 Open in sidebar (it used to be a second capsule stacked over
//                 this one — workspace AssistantAnswerPopover, retired). Once
//                 per answer; Esc, an outside press, ✕ and either move dismiss
//                 it (workspace functions/assistantAnswerPopover.js).
//
// STATE MODEL (T14): `voiceMinimized` (voice posture) and
// `assistantDock === 'minimized'` (drawer posture) stay INDEPENDENT root axes —
// unification happens at the SURFACE, not the state: this island renders
// whichever case is active, voice first (a live voice session is the
// foreground activity; its talk turns stream into the same thread the
// assistant cases preview, so voice visuals supersede them while open).
//
// Mounted at the workspace shell root (app.js, positioned via the injected
// `--slot-bottom` stylesheet rule) AND at the standalone canvas app root
// (canvas/app.js, `bottom: 'D'` set at the mount site — no shell vars there).
// On canvas only the voice case can activate: assistant/chat root keys don't
// exist and every read below guards for that; assistant-key WRITES happen only
// in assistant cases, which canvas can never reach (undeclared-root-key rule).
//
// The voice case bypasses BOTH the `aiAssistant` feature flag and the
// route-hides: the minimized voice card previously rendered on every route at
// VoiceOverlay's own z — the unified island must not regress that. Assistant
// cases keep the flag gate + route list exactly as before.
//
// The root is a DIV, not a button: the voice case nests real controls, and
// buttons-in-button is invalid DOM. The assistant cases' "whole pill opens the
// assistant" affordance moved to ExpandHit — a full-area transparent BUTTON
// child shown only in assistant cases, so keyboard/focus semantics stay native.
//
// Self-contained module helpers (matching the file's original pattern); the
// only imports are the feature flag (pre-existing) — every other piece is a
// bare key (AiBubbleStack, ChatToolStepLabel, AssistantIconButton, the voice
// pieces), and every shell move is a guarded `el.call`.

export const SimoneReasoningPill = {
  // FA204 / frank trap — liveMsg/runningTool/agents/runningAgents/isLive/
  // voiceCase/headLabel/voiceRowCount/threadMsgs/routePath used to be bare
  // module-scope const read from inside `hide`, `isChatPreview`, `isVoice`,
  // `onFrame` and the head/thread/activity factories (all serialized) — they
  // read as undefined once this component is frank.toJSON'd for platform
  // delivery. Moved into `scope`, called via
  // `el.scope.<name>` from factories and `this.<name>` between sibling scope
  // methods (object-literal properties can't see each other as bare
  // identifiers). `_routePath` is also HALF of the cross-file collision that
  // was blocking `smbls push` — Suggestions.js declared its own bare
  // module-scope `_routePath`, and esbuild's dedup-rename
  // (`_routePath`/`_routePath2`) made frank's verifier refuse the whole
  // publish. Scoping both resolves the collision (scope keys are per-
  // component, never bundled by name). The 2026-10-28 additions (approvalTool,
  // choiceCard, askWaits, stepTool, island, answerId, canMoveToMain,
  // answerDismiss, answerMove, statusText) follow the same law.
  scope: {
    // The live general assistant message (or null). Scans from the END — the
    // live message is the just-appended tail, and the transport re-sets
    // chatMessages on every delta, so a front `.find()` was an
    // O(thread-length) scan per tick across all the pill's factories.
    liveMsg(root) {
      if (!root || !root.currentAssistantId) return null
      const msgs = root.chatMessages || []
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i] && msgs[i].id === root.currentAssistantId) return msgs[i]
      }
      return null
    },

    // The last running tool of the live message (the current step), or null.
    runningTool(root) {
      const msg = this.liveMsg(root)
      if (!msg) return null
      const running = (msg.tools || []).filter((t) => t && t.status === 'running')
      return running.length ? running[running.length - 1] : null
    },

    // The step that waits for the person's OK, or null — the newest tool row
    // in the `approval` status among the assistant messages after the
    // person's last line. Read from the thread, not from the live id: a late
    // ask is anchored to the newest assistant message after the turn's id
    // cleared (simoneAiTransport._patchToolActivity), and it still holds the
    // server's loop. Mirrors AiAsk.scope.approvalTool, which renders the ask
    // itself.
    approvalTool(root) {
      const msgs = root && Array.isArray(root.chatMessages) ? root.chatMessages : []
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i]
        if (!m) continue
        if (m.role === 'user') return null
        const tools = m.role === 'assistant' && Array.isArray(m.tools) ? m.tools : []
        for (let j = tools.length - 1; j >= 0; j--) {
          if (tools[j] && tools[j].status === 'approval') return tools[j]
        }
      }
      return null
    },

    // The question that waits for the person's pick, or null — an ask_user
    // card (simoneAiTransport askUser: a confirm or a choice) nobody answered,
    // on an assistant message after the person's last line (a new line
    // closes it). The transport turns the typing off while it waits, so the
    // turn reads as done. Mirrors AiAsk.scope.choiceCard, which renders the
    // question itself.
    choiceCard(root) {
      const msgs = root && Array.isArray(root.chatMessages) ? root.chatMessages : []
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i]
        if (!m) continue
        if (m.role === 'user') return null
        const cards = m.role === 'assistant' && Array.isArray(m.cards) ? m.cards : []
        for (let j = cards.length - 1; j >= 0; j--) {
          const c = cards[j]
          const asks = !!c && (c.type === 'choice' || c.type === 'confirm')
          if (asks && c._cardId && !c._resolved) return c
        }
      }
      return null
    },

    // An ask waits on the person: a step for the OK, or a question for a pick.
    askWaits(root) {
      const tool = this.approvalTool(root)
      const card = this.choiceCard(root)
      return !!(tool || card)
    },

    // The step the head names after "Working:" — the running tool, unless the
    // delegated agents are the step or an ask waits (a question's own row
    // runs while it waits).
    stepTool(root) {
      if (this.askWaits(root) || this.runningAgents(root) > 0) return null
      return this.runningTool(root)
    },

    // The delegated/chained sub-agents of the current turn (general scope).
    agents(root) {
      return (root && root.activeProcesses) || []
    },
    runningAgents(root) {
      return this.agents(root).filter((p) => p && p.status === 'running').length
    },

    // Live = the main turn is streaming OR at least one sub-agent is still running.
    isLive(root) {
      return !!(root && (root.chatTyping || this.runningAgents(root) > 0))
    },

    // Voice island case — the minimized voice session owns the body. `voiceHost`
    // (a composer strip is hosting, T18) hands the surface to the composer.
    voiceCase(root) {
      return !!(root && root.voiceOpen && root.voiceMinimized && !root.voiceHost)
    },

    // Head label: an ask that waits outranks everything (the turn is stopped
    // on the person) — "Needs your OK" for a step, "Waiting for your choice"
    // for a question (the ask_user row's words) — then the agent count while
    // delegating, else Working (a tool is running) vs Thinking (pure
    // reasoning) — same rule and same words as ChatMessage.Dots.
    headLabel(root, el) {
      void root?.lang
      if (this.approvalTool(root)) return el.call('polyglot', 'ai.chat.needsOk')
      if (this.choiceCard(root)) return el.call('polyglot', 'ai.toolStep.choose')
      const running = this.runningAgents(root)
      if (running) {
        const label =
          running === 1
            ? el.call('polyglot', 'ai.pill.agentWorking')
            : el.call('polyglot', 'ai.pill.agentsWorking')
        return label.replace('{count}', String(running))
      }
      return this.runningTool(root)
        ? el.call('polyglot', 'ai.chat.working')
        : el.call('polyglot', 'ai.chat.thinking')
    },

    // Does the voice thread have anything in it? Mirrors VoiceLog's own source
    // precedence (voiceTurns, then the assistant thread) — a COUNT only; the
    // rendering lives in the shared component. Drives the island's width so
    // the gauge follows content instead of being pinned wide around an empty
    // log.
    voiceRowCount(root) {
      const live = (root && root.voiceTurns) || []
      if (live.length) return live.filter((t) => t && String(t.text || '').trim()).length
      const msgs = (root && root[(root.voiceChatKey || '') + 'chatMessages']) || []
      return msgs.filter(
        (m) => m && (m.role === 'user' || m.role === 'assistant') && String(m.text || '').trim()
      ).length
    },

    // The island's THREAD rows — the assistant conversation as a running log,
    // normalised into the shared VoiceLog row shape. This replaces the old
    // two-message RecentChat preview that was hidden the moment anything
    // started streaming: the conversation used to VANISH and be swapped for a
    // status line, then come back, which is the churn Nika reported
    // (2026-08-02, "ai assistant text islands in the background should be
    // consistant, logging in one small minimized thread so you can keep
    // track. do not show hide different ui"). Now it simply keeps growing —
    // the live reply is already in chatMessages with its text extending per
    // delta, so it streams in place inside the thread.
    //
    // 2026-10-28: the rows ride the bubbles ABOVE the bar now (AiBubbleStack,
    // which shows the newest two and fades the one before), so three are
    // enough. A streaming reply keeps its NEWEST words when it is cut — the
    // bubble shows the tail of a reply while it streams.
    threadMsgs(root) {
      const msgs = (root && root.chatMessages) || []
      const out = []
      for (let i = msgs.length - 1; i >= 0 && out.length < 3; i--) {
        const m = msgs[i]
        if (!m || (m.role !== 'user' && m.role !== 'assistant')) continue
        const t = typeof m.text === 'string' ? m.text.trim() : ''
        if (!t) continue
        const streaming = !!m.streaming
        out.unshift({
          id: m.id || 'm' + i,
          role: m.role === 'user' ? 'user' : 'agent',
          text: t.length <= 420 ? t : streaming ? '…' + t.slice(-420) : t.slice(0, 420) + '…',
          streaming
        })
      }
      return out
    },

    // ——— the answer that landed while the island is the thread's handle ———
    // The island element: the answer baseline lives on it (onInit), and a
    // child's factory reaches it through the element tree.
    island(el) {
      if (!el) return null
      if (el.__aiIsland) return el
      return typeof el.lookup === 'function' ? el.lookup((p) => !!(p && p.__aiIsland)) : null
    },

    // The answer the head offers ("Answer ready"), or null. The shell owns the
    // rule (workspace functions/assistantAnswerPopover.js): an answer that
    // landed after the island mounted, while the island is the handle, not
    // dismissed. A host without it (the standalone canvas) has no answer
    // state.
    answerId(el, root) {
      const fns = el && el.context && el.context.functions
      if (!fns || typeof fns.assistantAnswerPopoverFor !== 'function') return null
      const isl = this.island(el)
      return (
        el.call('assistantAnswerPopoverFor', root, isl ? isl.__answerBaseline : undefined) || null
      )
    },

    canMoveToMain(el, root) {
      const fns = el && el.context && el.context.functions
      if (!fns || typeof fns.assistantThreadCanMoveToMain !== 'function') return false
      return !!el.call('assistantThreadCanMoveToMain', root)
    },

    answerDismiss(el) {
      const fns = el && el.context && el.context.functions
      if (fns && typeof fns.assistantAnswerPopoverDismiss === 'function') {
        el.call('assistantAnswerPopoverDismiss')
      }
    },

    // Open in main / Open in sidebar — the shell's two moves (each dismisses
    // the answer). The slot handle retires with either: the thread has a real
    // surface now, so the island must not come back when it closes.
    answerMove(el, s, where) {
      const root = s && s.root
      if (!root || typeof root.update !== 'function') return
      const fns = (el.context && el.context.functions) || {}
      const name = where === 'main' ? 'assistantAnswerToMain' : 'assistantAnswerToSidebar'
      if (typeof fns[name] === 'function') el.call(name)
      if (root._assistantPillHandle) root.update({ _assistantPillHandle: false })
    },

    // The head's status words: the ask, the live turn, the landed answer, or
    // the idle handle's name. A live turn names its running step after a
    // colon (the step itself is the HeadStep span beside these words). A
    // question that waits is never "Answer ready": its turn is not done.
    statusText(el, root) {
      if (this.askWaits(root)) return this.headLabel(root, el)
      if (this.isLive(root)) return this.headLabel(root, el) + (this.stepTool(root) ? ':' : '…')
      if (this.answerId(el, root)) return el.call('polyglot', 'ai.pill.answerReady')
      return el.call('polyglot', 'ai.pill.idle')
    },

    // Route WITHOUT the multi-workspace-tabs `/w/<slug>` prefix — the hide's
    // route list compares bare app routes, and on a prefixed tab
    // (`/w/acme/add-app`) the raw path never matches. Inline mirror of
    // workspacePrefix.js stripWorkspacePrefix.
    routePath(root) {
      const p =
        (root && root.currentPath) ||
        (typeof window !== 'undefined' ? window.location.pathname : '/')
      const stripped = String(p).replace(/^\/w\/[a-z0-9-]{1,48}(?=\/|$)/, '')
      return stripped === '' ? '/' : stripped
    }
  },

  // Show when something needs the small surface. Voice case first — it bypasses
  // the aiAssistant flag and the route hides (see header). Assistant cases keep
  // the original logic verbatim: the FULL drawer isn't visible AND there's
  // either live activity, the persistent minimized handle, or the slot handle —
  // or, since 2026-10-28, a step that waits for the person's OK.
  // The keys the gate reads only behind a branch are declared, so every run
  // subscribes to them whichever branch returns first (FA515).
  hideDeps: [
    (el, s) => s.root && s.root.voiceHost,
    (el, s) => s.root && s.root.voiceMinimized,
    (el, s) => s.root && s.root.meetView,
    (el, s) => s.root && s.root.assistantDock,
    (el, s) => s.root && s.root._assistantPillHandle
  ],
  hide: (el, s) => {
    const r = s.root
    if (!r) return true
    if (el.scope.voiceCase(r)) return false
    // Voice in the FOREGROUND elsewhere — the full overlay or a composer strip
    // (`voiceHost` alone keeps the strip visible: it ignores the minimized
    // axis) — every assistant case yields. The strip already streams the same
    // thread this pill would preview (command turns mirror into voiceTurns),
    // so a live pill or the minimized-drawer handle beside it is the same
    // conversation on screen twice (2026-08-06 per Nika "when voice is open,
    // no point to have chat - they are morphing into each other"). Restores
    // itself the moment voice minimizes (the voice case above takes the
    // island) or closes (normal assistant logic resumes).
    if (r.voiceOpen && (r.voiceHost || !r.voiceMinimized)) return true
    // /flags gate: assistant off removes the pill everywhere.
    if (!featureEnabled(r, 'aiAssistant')) return true
    // On routes where the global composer is hidden — they own their bottom UI
    // (/chat, /add-app) or the pill would overlap call controls (an active /meet
    // call, /meet-guest) — the pill, which anchors just above that composer, must
    // hide too; otherwise it floats over the page's own bottom UI at a stale
    // --slot-bottom (the composer's ResizeObserver freezes while it's hidden).
    // Mirrors CanvasPromptTextarea.hide's route list; reading currentPath also
    // makes the pill re-evaluate on a pure route change (it reads no other route
    // signal). Routes absent on the standalone extension simply never match.
    const path = el.scope.routePath(r)
    if (path === '/add-app' || path === '/chat' || path === '/meet-guest') return true
    if (path === '/meet' && r.meetView === 'call') return true
    if (r.assistantOpen && r.assistantDock !== 'minimized') return true
    if (r._homeThreadOpen) return true
    const minimizedHandle = r.assistantOpen && r.assistantDock === 'minimized'
    // Third handle case: the shell DELIBERATELY kept the drawer closed for this
    // turn because a page sheet is open in the PageSlot and the drawer would
    // cover it (homeAiSend sets _assistantPillHandle). The pill is then the
    // turn's ONLY surface, so it must outlive the turn exactly like the
    // minimized handle does — otherwise the send looks silently dropped: the
    // composer clears, the pill shows while streaming, and then everything
    // (message + answer) disappears with no thread entry and no error.
    const slotHandle = !r.assistantOpen && !!r._assistantPillHandle
    // An ask waits on the person — a step for the OK, a question for a pick —
    // and the thread is off screen (the two checks above): the island is the
    // ask's only surface, so it shows even with no handle and no live turn —
    // the transport stops "typing" while an ask waits (simoneAiTransport
    // onWaiting, askUser), which used to hide it.
    if (el.scope.askWaits(r)) return false
    return !el.scope.isLive(r) && !minimizedHandle && !slotHandle
  },
  // Thread footprint — whenever the thread has anything in it, idle handle OR
  // mid-turn, the island matches the global composer it docks above: the same
  // `H` width token as CanvasPromptTextarea (and the same full-width @mobileL
  // treatment), so it reads as part of the composer stack. Pinning the gauge
  // across BOTH states is what stops the island resizing every time a turn
  // starts and ends — `width: max-content` around a growing reply would jump
  // the footprint on every delta, which reads as a different panel rather than
  // the same one filling up. A conditional class, NOT a reactive `width`
  // factory — reactive layout props don't re-apply on a state change in v3.14
  // (same mechanism as the composer's `.isLarge`).
  // Width follows the THREAD, in either mode. Measured on the deployed build
  // the voice posture was pinned at 494px with the log hidden and two of seven
  // controls hidden — half the island was empty space, which is why it read as
  // a panel rather than a compact handle. Now it is content-sized until there
  // is actually a transcript to hold, then it takes the composer gauge.
  // An ask that waits takes the gauge too: its bubble spans the bar's width.
  isChatPreview: (el, s) =>
    el.scope.voiceCase(s.root)
      ? el.scope.voiceRowCount(s.root) > 0
      : el.scope.threadMsgs(s.root).length > 0 || el.scope.askWaits(s.root),
  '.isChatPreview': {
    width: 'H',
    '@mobileL': {
      left: 'A',
      right: 'A',
      width: 'auto',
      maxWidth: 'calc(100dvw - var(--spacing-B))',
      transform: 'none'
    }
  },
  // THE COMPOSER'S GAUGE — whenever the island docks above the composer as one
  // stack (the call bar, or a thread preview), the shell sizes it to the
  // composer's LIVE width: workspace app.js publishes the resting pill's width
  // as --prompt-w and its injected stylesheet reads this attribute (wider than
  // a phone; a phone keeps the island's own left/right inset). The pill grows
  // past `H` with its context chip, so an `H` island sat 10px in on each side.
  // A data attribute, not the class blocks below: those are hashed and paint
  // their width inline, so only a shell rule keyed on a stable attribute can
  // override it.
  data: {
    gauge: (el, s) =>
      el.scope.voiceCase(s.root) ||
      el.scope.threadMsgs(s.root).length > 0 ||
      el.scope.askWaits(s.root)
        ? 'composer'
        : null
  },

  // Voice posture — THE CALL BAR (2026-10-22, the call redesign): the call's
  // row (orb · status and clock · controls) at the composer's own `H` gauge
  // from the first frame, so the bar never jumps wide when the first line
  // lands (the controls now share the head row, so the bar is never a wide,
  // mostly empty card either). Equal padding on every side; no pointer
  // affordance on the shell itself (the controls are the hit targets).
  isVoice: (el, s) => el.scope.voiceCase(s.root),
  '.isVoice': {
    width: 'H',
    cursor: 'default',
    gap: 'Z',
    padding: 'Z',
    '@mobileL': {
      left: 'A',
      right: 'A',
      width: 'auto',
      maxWidth: 'calc(100dvw - var(--spacing-B))',
      transform: 'none'
    }
  },
  position: 'fixed',
  // Above the PageSlot (z 1300) so the small view stays visible when a page sheet
  // is open; below modals. This also holds for the voice case — the island stays
  // at 1350 (NOT the overlay's 10600): minimized voice is ambient, it must not
  // outrank dialogs. The BOTTOM offset is set in app.js's injected shell CSS
  // (`[data-key="SimoneReasoningPill"]{bottom:var(--slot-bottom)}`) — a raw
  // `var(--…)` on `bottom` compiles to an EMPTY DOMQL atomic rule (see
  // calc-width-empty-atomic-rule), so shell-var positioning MUST go through the
  // stylesheet, exactly like the PageSlot's own --slot-bottom. --slot-bottom =
  // --prompt-clear (composer clearance, live via RO) + --nav-gap, so the pill's
  // bottom edge sits the same nav-gap above the composer as the PageSlot's does.
  // (The standalone canvas mount sets `bottom: 'D'` at the mount site instead.)
  zIndex: '1350',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 'max-content',
  maxWidth: '92vw',
  flow: 'y',
  gap: 'Z',
  padding: 'Z Z1',
  theme: 'canvas-card-glass',
  // Same surface treatment as the EXPANDED composer (2026-08-02 per Nika
  // "voice ui should be similar to expanded chattextarea ui — no border,
  // similar rounds"): the island is the assistant/voice surface changing
  // posture, not a foreign card stacked on the page. The border is zeroed and
  // elevation comes from the theme, exactly as CanvasPromptTextarea does it.
  //
  // The RADIUS deliberately does NOT copy the composer's token (2026-08-02 per
  // Nika "round is too big for voice island"). Matching `round: 'B'` literally
  // was the first attempt and it looked wrong, because a corner radius is
  // absolute while these two boxes differ ~3x in height. With the brand
  // spacing scale (base 17, ratio 1.618) B is ~27.5 and A is ~17: on the
  // expanded composer — `width: 'H'`, many lines tall — 27.5 is a modest card
  // corner, but on this island (one control row inside `padding: 'Z Z1'`,
  // ~48 tall) 27.5 exceeds half the height, so it collapses into a full
  // capsule and reads as a different species of surface. `A` keeps a soft
  // card corner at BOTH island heights — the compact idle handle and the
  // taller transcript posture — which is what "match the composer" means
  // perceptually. Note this is NOT the em-basis trap (Rule 34): both mount as
  // siblings at the app root (workspace/app.js SimoneReasoningPill + Canvas-
  // PromptTextarea) with no fontSize of their own, so the token resolves to
  // identical pixels on each — the difference is purely box geometry.
  round: 'A',
  // canvas-card-glass paints a 1px border via the brand theme; this chrome
  // reads cleaner without it. Longhand `borderWidth: 0` — the theme's
  // longhand `borderWidth: 1px` lands after a shorthand when DOMQL composes
  // the cascade, so `border: none` is a no-op (the trap the composer and the
  // mode tabs both document).
  borderWidth: '0',
  // No explicit boxShadow: the theme already carries the elevation pair
  // (inset sheen + `0 4px 32px`), which is the exact elevation the expanded
  // composer floats on. The previous hard-coded rgba both double-darkened
  // this surface relative to the composer and violated tokens-only.
  cursor: 'pointer',
  textAlign: 'left',
  // Morph discipline: opacity + padding tween between postures (both inert —
  // nothing drives them per-frame); `bottom` must stay OUT of the list (driven
  // by the live --prompt-clear var — `all` made it never settle), and width
  // swaps max-content↔H which no transition can tween — the posture keyframes
  // on the body groups carry that swap visually instead.
  transition: 'B opacity defaultBezier, B padding defaultBezier',

  // The call chrome's 60 fps half — the orb's ring and the clock — runs off
  // the shared driver (functions/voiceSession.js voiceCallFrame), only while
  // the island IS the call (it reads the analyser off the VoiceOverlay, the
  // session host, and caches its refs on this element).
  onFrame: (el, s) => {
    const root = (s && s.root) || s
    // FA210: the frame loop can tick before this element's scope exists
    // (early frames / bypass mounts).
    if (!root || !el.scope || !el.scope.voiceCase(root)) return
    el.call('voiceCallFrame')
  },

  // ——— the landed answer (the retired AssistantAnswerPopover's rules) ———
  // The newest answer at mount is the BASELINE: a restored thread never says
  // "Answer ready", only an answer that lands after. Kept on the element
  // (children reach it through scope.island). Guarded: the standalone canvas
  // has no answer state.
  onInit: (el, s) => {
    el.__aiIsland = true
    const fns = el.context && el.context.functions
    if (fns && typeof fns.assistantLatestAnswerId === 'function') {
      el.__answerBaseline = el.call('assistantLatestAnswerId', s && s.root)
    }
  },
  // Esc dismisses it — a row of the shell keyboard registry, live only while
  // the answer shows.
  onMounted: (el) => {
    const fns = el.context && el.context.functions
    if (fns && typeof fns.assistantAnswerPopoverShortcuts === 'function') {
      el.call('assistantAnswerPopoverShortcuts', el)
    }
  },
  onBeforeRemove: (el) => {
    if (typeof el.__popoverShortcutsOff === 'function') el.__popoverShortcutsOff()
  },
  // A press outside the island dismisses it (a read-only listener).
  onDocumentPointerdown: (e, el, s) => {
    if (!el.scope || typeof el.scope.answerId !== 'function') return
    if (!el.scope.answerId(el, s && s.root)) return
    const node = el.node
    if (node && e && e.target && typeof node.contains === 'function' && node.contains(e.target))
      return
    el.scope.answerDismiss(el)
  },

  // ——— assistant cases' whole-surface affordance ———
  // Full-area button (valid: the root is a div) shown only while an assistant
  // case owns the island. Native focus/keyboard for free; the voice case hides
  // it so the voice controls receive their own clicks.
  ExpandHit: {
    tag: 'button',
    type: 'button',
    display: null,
    show: (el, s) => !el.scope.voiceCase(s.root),
    position: 'absolute',
    inset: '0',
    zIndex: '1',
    background: 'transparent',
    borderWidth: '0',
    padding: '0',
    cursor: 'pointer',
    title: (el) => el.call('polyglot', 'ai.pill.open'),
    aria: { label: (el) => el.call('polyglot', 'ai.pill.open') },
    // Tapping the pill expands back to the full drawer. If the assistant was
    // minimized, restore it to a visible dock (right) so the drawer actually
    // shows; otherwise just open at the current dock.
    onClick: (e, el, s) => {
      // Expanding retires the slot handle — the drawer itself now carries the
      // thread, so the pill must not re-appear if the user later closes it.
      // Written only when the flag is actually set: hosts that don't declare it
      // (preview / standalone) must not take an undeclared-root-key write.
      const patch = { assistantOpen: true }
      if (s.root._assistantPillHandle) patch._assistantPillHandle = false
      if (s.root.assistantDock === 'minimized') patch.assistantDock = 'right'
      s.root.update(patch)
      // The drawer shows the answer now: "Answer ready" is read.
      el.scope.answerDismiss(el)
    }
  },

  // THE CONSTANT CHROME. One head row for every state the island can be in —
  // idle handle, live turn, an ask, a landed answer, and voice. It is never
  // hidden and never swapped; only its leading indicator, its words and its
  // trailing buttons change. There used to be TWO heads (this one plus a
  // separate VoiceHead) that hid each other, so entering voice read as one
  // panel being replaced by another instead of the same object transforming
  // (2026-08-02 per Nika "in terms of voice and text, island should be
  // transforming").
  //
  // AT THE CONTROL HEIGHT, ALWAYS (2026-10-28): the row is `controlHCompact`
  // tall whether or not a 36px button is on show in it, so the bar is the
  // same height idle, live, asking, answered and in a call.
  Head: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    width: '100%',
    minWidth: '0',
    minHeight: 'controlHCompact',

    Lead: {
      flow: 'x',
      align: 'center flex-start',
      gap: 'Y',
      minWidth: '0',
      flex: '1 1 auto',

      // Star and orb are the two faces of the same slot — one leaves as the
      // other arrives, so the row's geometry never changes.
      // Plain `fadeIn`, not the `fadeInUpShort` the old swapped-in voice blocks
      // used: these two occupy the SAME slot, so a rise would read as arriving
      // from elsewhere. Cross-fading in place is what makes it one indicator
      // changing rather than two indicators trading places. 150ms is the brand
      // base — quick and physical, opacity only.
      // The star blinks while the assistant works (a turn streams or agents
      // run); an ask that waits is the person's move, so it stands still.
      Icon: {
        display: null,
        show: (el, s) => !el.scope.voiceCase(s.root),
        name: 'magicstar',
        boxSize: 'Z',
        color: 'title',
        flexShrink: '0',
        animationName: 'fadeIn',
        animationDuration: '150ms',
        isWorking: (el, s) => el.scope.isLive(s.root) && !el.scope.askWaits(s.root),
        '.isWorking': {
          animationName: 'blinking',
          animationDuration: '1.6s',
          animationIterationCount: 'infinite'
        }
      },

      // The CALL's face of the same slot (2026-10-22, the call redesign): the
      // shared orb at the control height, its ring driven by the island's
      // frame loop (voiceCallFrame). Replaced a 13-bar wave that read as a
      // dashed rule at rest.
      VoiceOrb: {
        display: null,
        show: (el, s) => el.scope.voiceCase(s.root),
        animationName: 'fadeIn',
        animationDuration: '150ms'
      },

      // The call's status — the shared word + clock (localized; this slot
      // used to print hard-coded English in every language).
      VoiceCallStatus: {
        display: null,
        show: (el, s) => el.scope.voiceCase(s.root),
        flex: '1 1 auto',
        animationName: 'fadeIn',
        animationDuration: '150ms'
      },

      // The status words: Thinking… · Working: · 2 agents working… · Needs
      // your OK · Answer ready · Assistant. One line; the tooltip is the whole
      // of it (the overflow law, §3b rule 8).
      Label: {
        tag: 'span',
        display: null,
        show: (el, s) => !el.scope.voiceCase(s.root),
        flexShrink: '0',
        maxWidth: '100%',
        minWidth: '0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 'Z1',
        fontWeight: '500',
        color: 'title',
        text: (el, s) => el.scope.statusText(el, s.root),
        title: (el, s) => el.scope.statusText(el, s.root)
      },

      // The running step, in the tool row's own words ("Searching
      // “transactions”") — named in the row instead of a second row under
      // it. Cut to one line here; the drawer's timeline holds the rest.
      HeadStep: {
        tag: 'span',
        flow: 'x',
        hide: (el, s) => el.scope.voiceCase(s.root) || !el.scope.stepTool(s.root),
        flex: '0 1 auto',
        minWidth: '0',
        fontSize: 'Z1',
        color: 'paragraph',
        childExtends: 'ChatToolStepLabel',
        childrenAs: 'state',
        children: (el, s) => {
          const tool = el.scope.stepTool(s.root)
          return tool ? [{ ...tool, key: 'step-' + tool.id }] : []
        },
        childProps: { oneLine: true }
      }
    },

    // VOICE-TALK-ONLY-SIMONE-1 part 2 (Nika: "remove command and talk
    // switcher - just keep talk"): the island's VoiceModeTabs trailing slot
    // is unmounted — Talk is the only user-facing mode now (voiceSession
    // voiceStart talk-default; the composer's hold-to-command gesture is the
    // one remaining one-shot path).

    // The call's controls ride the head row's trailing edge (the Fly call
    // bar's anatomy): the SHARED set the composer strip carries too, each
    // button deriving its own visibility from state — here Mute, Expand and
    // End call (Type shows only while a composer hosts).
    VoiceControls: {
      display: null,
      show: (el, s) => el.scope.voiceCase(s.root),
      animationName: 'fadeIn',
      animationDuration: '150ms'
    },

    // "Answer ready"'s two moves, in the row (they were a second capsule
    // stacked over this one). Icon buttons on the assistant chrome's own
    // primitive; their names are their tooltips. Above ExpandHit (z 2 vs 1).
    AssistantIconButton_main: {
      hide: (el, s) =>
        el.scope.voiceCase(s.root) ||
        !el.scope.answerId(el, s.root) ||
        !el.scope.canMoveToMain(el, s.root),
      icon: 'lnGrid',
      title: (el) => el.call('polyglot', 'ai.pill.openInMain'),
      position: 'relative',
      zIndex: '2',
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.scope.answerMove(el, s, 'main')
      }
    },
    AssistantIconButton_side: {
      hide: (el, s) => el.scope.voiceCase(s.root) || !el.scope.answerId(el, s.root),
      icon: 'sidebarRightFill',
      title: (el) => el.call('polyglot', 'ai.pill.openInSidebar'),
      position: 'relative',
      zIndex: '2',
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.scope.answerMove(el, s, 'side')
      }
    },

    // Trailing dismiss (2026-09-02 per Nika: "it should be possible to
    // close/minimize ExpandHit"). The idle handle used to be permanent — the
    // only affordance was ExpandHit, so a pill you did not want could only be
    // resolved by opening the drawer and closing THAT. This X retires both
    // handle cases in one gesture, and the landed answer with them. It sits
    // ABOVE ExpandHit (zIndex 2 vs 1) so the X wins its own clicks; hidden
    // while a turn is live or an ask waits, because the pill is the turn's
    // only surface then and the hide gate would keep it on screen regardless.
    // Voice has its own close in VoiceControls.
    AssistantIconButton_close: {
      hide: (el, s) =>
        el.scope.voiceCase(s.root) || el.scope.isLive(s.root) || el.scope.askWaits(s.root),
      icon: 'crossmark',
      title: (el) => el.call('polyglot', 'ai.pill.dismiss'),
      aria: { label: (el) => el.call('polyglot', 'ai.pill.dismissLabel') },
      position: 'relative',
      zIndex: '2',
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.scope.answerDismiss(el)
        // Retire every handle that keeps the pill mounted: the minimized-dock
        // handle (assistantOpen + dock 'minimized') and the slot handle. Only
        // declared keys are written (undeclared-root-key rule — hosts without
        // the pill-handle flag never take that write).
        const patch = { assistantOpen: false }
        if (s.root._assistantPillHandle) patch._assistantPillHandle = false
        s.root.update(patch)
      }
    }
  },

  // ——— above the bar: the words as bubbles, and the ask ———
  // The shared stack (AiBubbleStack, the composer's call strip mounts the same
  // one). The island hands it the assistant conversation in the text posture;
  // in a call it reads the call's own turns. It replaced the in-row thread
  // (VoiceLog + the live partial + CurrentStep + Agents), which grew the bar
  // with every line.
  AiBubbleStack: {
    turnsSource: (el, s) => (el.scope.voiceCase(s.root) ? null : el.scope.threadMsgs(s.root))
  }
}
